import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatGoalValue, localToday, lastActivityText } from '@battleops/shared';
import { useHomeProjects } from '@battleops/client/src/data/useDeptGoals';
import { useTaskModal } from '../../hooks';
import { OTHER } from '../charts';
import { distinctColors } from '../../lib/colors';
import { TONE } from './DeptParts';
import Icon from '../Icon';
import { ServerLine } from '../ServerCard';
import s from './PinnedProjects.module.css';

// The app projects pinned to a department's Home (Lj, Oct 11: "add app projects to the home screen", "replace Next
// moves with app projects", "make them editable so we can add and remove them", "give the cards like info for that
// app"): each an information card in Home's card chrome — the project hero (icon, colour wash, name), hours this week
// (live) with the last 7 days as bars, open tasks, open #bug, shipped, not live, the next task on the schedule. Add (+
// card → picker), Edit (× badges, reorder), ⋯ (remove / move). Stored as the department's `homeProjectIds`, in order;
// removing a pin never touches the project. Figures from shared projectCard (counted the way the Dev cards count).
// Every department's projects are cards (v0.1.256, Lj: "personal projects should be cards like the app section"): one
// AppCard with a role switch — Dev: open · bugs · shipped · not live; everyone else: open · done this week · last
// activity — and the words by role (homePinsCopy: Apps / Jobs / Projects). No projects at all = one compact row.

const H = 3600000;
const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dayName = (date) => DAY[new Date(`${date}T12:00:00`).getDay()];
const short = (date) => new Date(`${date}T12:00:00`).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
const hrsTxt = (ms) => formatGoalValue(ms / H, 'h');

export function Picker({ choices, onPick, onClose, title = 'Add a project to Home' }) {
  const [q, setQ] = useState('');
  useEffect(() => { const k = (e) => { if (e.key === 'Escape') onClose(); }; window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k); }, [onClose]);
  const f = (list) => list.filter((p) => !q || String(p.name || '').toLowerCase().includes(q.toLowerCase()));
  const item = (p) => (
    <button key={p.id} type="button" className={s.pick} onClick={() => onPick(p.id)}>
      <span className={s.pickDot} style={{ background: p.color || OTHER }} />{p.icon ? <span>{p.icon}</span> : null}<span className={s.rowText}>{p.name}</span><Icon name="plus" size={15} />
    </button>
  );
  return (
    <div className={s.scrim} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={s.sheet} role="dialog" aria-modal="true" aria-label={title}>
        <div className={s.sheetHead}><b>{title}</b><button type="button" className={s.iconBtn} onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button></div>
        <input className={s.search} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a project" autoFocus />
        {f(choices.here).length ? <><div className={s.pickGroup}>In this department</div>{f(choices.here).map(item)}</> : null}
        {f(choices.others).length ? <><div className={s.pickGroup}>All projects</div>{f(choices.others).map(item)}</> : null}
        {!f(choices.here).length && !f(choices.others).length ? <div className={s.hint} style={{ padding: 12 }}>No other live projects.</div> : null}
      </div>
    </div>
  );
}

/** A pinned project's information card: the project hero (icon, colour wash, name), its figures, hours by day. */
export function AppCard({ card: c, color, editing, first, last, onRemove, onMove, onOpenProject, onOpenTask, compact = false }) {
  const p = c.project;
  const [menu, setMenu] = useState(false);
  const [show, setShow] = useState(null);   // dev: 'bugs' | 'shipped' | 'waiting' · projects: 'done'
  const dev = c.role !== 'projects';
  const ref = useRef(null);
  useEffect(() => {
    if (!menu) return undefined;
    const k = (e) => { if (!ref.current?.contains(e.target)) setMenu(false); };
    window.addEventListener('mousedown', k); return () => window.removeEventListener('mousedown', k);
  }, [menu]);
  const today = localToday();
  const max = Math.max(1, ...c.days.map((d) => d.ms));
  const fig = (key, label, value, list, tone) => (
    <button type="button" className={s.fig} data-on={show === key || undefined} disabled={!list?.length} onClick={() => setShow(show === key ? null : key)} aria-label={`${label}: ${value}`}>
      <b style={value && tone ? { color: tone } : null}>{value}</b><span>{label}</span>
    </button>
  );
  const listed = show ? c[show] : null;
  return (
    <article className={s.app} data-compact={compact || undefined} style={{ '--pc': color }} ref={ref}>
      {editing ? <button type="button" className={s.unpin} onClick={onRemove} aria-label={`Remove ${p.name} from Home`}><Icon name="x" size={13} /></button> : null}
      <div className={s.appHead}>
        <button type="button" className={s.appName} onClick={onOpenProject}>
          <span className={s.appIcon}>{p.icon || <Icon name="folder" size={16} />}</span>
          <span className={s.rowText}>{p.name}</span>
        </button>
        {editing ? (
          <span className={s.moveBtns}>
            <button type="button" className={s.iconBtn} disabled={first} onClick={() => onMove(-1)} aria-label="Move earlier"><Icon name="chevron-left" size={15} /></button>
            <button type="button" className={s.iconBtn} disabled={last} onClick={() => onMove(1)} aria-label="Move later"><Icon name="chevron-right" size={15} /></button>
          </span>
        ) : (
          <span className={s.menuWrap}>
            <button type="button" className={s.iconBtn} onClick={() => setMenu(!menu)} aria-label={`${p.name} options`} aria-expanded={menu}><Icon name="more-horizontal" size={16} /></button>
            {menu ? (
              <span className={s.menu} role="menu">
                <button type="button" role="menuitem" onClick={() => { setMenu(false); onOpenProject(); }}><Icon name="external-link" size={14} />Open project</button>
                {!first ? <button type="button" role="menuitem" onClick={() => { setMenu(false); onMove(-1); }}><Icon name="chevron-left" size={14} />Move earlier</button> : null}
                {!last ? <button type="button" role="menuitem" onClick={() => { setMenu(false); onMove(1); }}><Icon name="chevron-right" size={14} />Move later</button> : null}
                <button type="button" role="menuitem" className={s.danger} onClick={() => { setMenu(false); onRemove(); }}><Icon name="x" size={14} />Remove from Home</button>
              </span>
            ) : null}
          </span>
        )}
      </div>
      <div className={s.appTime}>
        <div>
          <b className={s.appHours}>{hrsTxt(c.weekMs)}</b><span className={s.hintInline}> this week</span>
          {c.running ? <button type="button" className={s.running} onClick={() => onOpenTask(c.running.id)}><span className={s.pulse} /><span className={s.rowText}>{c.running.title}</span></button> : null}
        </div>
        <svg viewBox="0 0 70 30" className={s.appSpark} role="img" aria-label={`Hours, last 7 days: ${c.days.map((d) => `${dayName(d.date)} ${hrsTxt(d.ms)}`).join(', ')}`}>
          {c.days.map((d, i) => { const h = d.ms ? Math.max(3, (d.ms / max) * 28) : 2; return <rect key={d.date} x={i * 10} y={30 - h} width={7} height={h} rx={1.5} fill={d.ms ? color : 'var(--c-border-strong)'} fillOpacity={d.ms ? (i === 6 ? 1 : 0.6) : 0.6}><title>{`${short(d.date)}: ${hrsTxt(d.ms)}`}</title></rect>; })}
        </svg>
      </div>
      <ServerLine project={p} />{/* the VPS: RAM · disk free · uptime (owners only) */}
      <div className={s.figs} data-role={c.role}>
        <button type="button" className={s.fig} onClick={onOpenProject} aria-label={`Open tasks: ${c.open}. Open the project`}><b>{c.open}</b><span>open</span></button>
        {dev ? (
          <>
            {fig('bugs', 'bugs', c.bugs.length, c.bugs, TONE.close)}
            {fig('shipped', 'shipped', c.shipped.length, c.shipped, TONE.met)}
            {fig('waiting', 'not live', c.waiting.length, c.waiting, TONE.behind)}
          </>
        ) : (
          <>
            {fig('done', 'done · week', c.done.length, c.done, TONE.met)}
            <div className={s.fig} data-wide aria-label={`Last activity: ${lastActivityText(c.lastAt)}`}><b className={s.figText}>{lastActivityText(c.lastAt)}</b><span>last activity</span></div>
          </>
        )}
      </div>
      {listed?.length ? (
        <div className={s.appList}>
          {listed.slice(0, 5).map((t) => <button key={t.id} type="button" className={s.row} onClick={() => onOpenTask(t.id)}><span className={s.rowText}>{t.title}</span><Icon name="chevron-right" size={13} /></button>)}
        </div>
      ) : null}
      <button type="button" className={s.next} disabled={!c.next} onClick={() => c.next && onOpenTask(c.next.task.id)}>
        <span className={s.nextLabel} style={c.next?.overdue ? { color: TONE.over } : null}>{c.next ? (c.next.overdue ? `OVERDUE · ${dayName(c.next.date)}` : c.next.date === today ? 'NEXT · TODAY' : `NEXT · ${dayName(c.next.date)}`) : 'NEXT'}</span>
        <span className={s.rowText}>{c.next ? c.next.task.title : 'Nothing on the schedule'}</span>
      </button>
    </article>
  );
}

/** The pinned projects with Add / Edit (Lj, Oct 11: "make them editable so we can add and remove them"), worded by role. */
export function PinnedProjects({ dept, compact = false }) {
  const navigate = useNavigate();
  const details = useTaskModal();
  const { cards, choices, pin, unpin, move, copy, hasProjects } = useHomeProjects(dept);
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false);
  const colors = useMemo(() => distinctColors(cards.map((c) => c.project.id), (id) => cards.find((c) => c.project.id === id)?.project.color), [cards]);
  const picker = picking ? <Picker title={copy.picker} choices={choices} onClose={() => setPicking(false)} onPick={async (id) => { await pin(id); setPicking(false); }} /> : null;
  if (!cards.length && !hasProjects) {
    // nothing in this department at all — one compact row, not the big dashed card
    return (
      <section className={s.apps} data-compact={compact || undefined}>
        <div className={s.emptyRow}>
          <span className={s.rowText}>{copy.empty}</span>
          <button type="button" className={s.emptyAdd} onClick={() => setPicking(true)}><Icon name="plus" size={15} />{copy.add}</button>
        </div>
        {picker}
      </section>
    );
  }
  return (
    <section className={s.apps} data-compact={compact || undefined}>
      <div className={s.secHead}>
        <div><h2>{copy.title}</h2><span className={s.secSub}>{copy.sub}</span></div>
        {cards.length ? <button type="button" className={s.textBtn} onClick={() => setEditing(!editing)} aria-pressed={editing}>{editing ? 'Done' : 'Edit'}</button> : null}
      </div>
      <div className={s.appGrid}>
        {cards.map((c, i) => (
          <AppCard key={c.project.id} card={c} color={colors.get(c.project.id) || OTHER} editing={editing} first={i === 0} last={i === cards.length - 1} compact={compact}
            onRemove={() => unpin(c.project.id)} onMove={(dir) => move(c.project.id, dir)}
            onOpenProject={() => navigate(`/projects/${c.project.id}`)} onOpenTask={(id) => details.open(id)} />
        ))}
        <button type="button" className={s.addApp} onClick={() => setPicking(true)}>
          <span className={s.addIcon}><Icon name="plus" size={18} /></span>
          <b>{copy.add}</b>
          <span className={s.hint}>{cards.length ? copy.more : copy.first}</span>
        </button>
      </div>
      {picker}
    </section>
  );
}
